import { useCallback } from 'react';

import { css, styled } from '@mui/material';

import { useOptions } from '../../hooks';
import { useCascadeState } from '../../utils';
import { segmentedControlClasses } from './theme';

import type { ChangeEvent } from 'react';
import type {
  SegmentedControlOption,
  SegmentedControlOwnerState,
  SegmentedControlProps,
} from './types';

const SegmentedControl = function <OptionValue, ExtraOption extends {} = {}>(
  props: SegmentedControlProps<OptionValue, ExtraOption>,
) {
  type Option = SegmentedControlOption<OptionValue, ExtraOption>;

  const {
    defaultValue,
    disabled,
    inputRef,
    name,
    onChange: onChangeProp,
    onReselect,
    options: optionsProp,
    value: valueProp,
    ...others
  } = props;

  const ownerState: SegmentedControlOwnerState = props as SegmentedControlProps<unknown>;

  const [value, onChange] = useCascadeState({
    initialValue: defaultValue ?? null,
    value: valueProp,
    onChange: (value: OptionValue | null, event: ChangeEvent<HTMLInputElement>) => {
      if (value !== null) {
        onChangeProp?.(event, value);
      }
    },
  });

  const { options, getOptionDisabled, getOptionSelected } = useOptions({
    options: optionsProp,
    getOptionDisabled: option => option.disabled,
    getOptionSelected: option => option.value === value,
  });

  const getOptionLabelText = useCallback((option: Option): React.ReactNode => {
    return option.label ?? String(option.value);
  }, []);

  const getOptionAriaLabel = useCallback((option: Option): string | undefined => {
    if (typeof option === 'object') {
      return option['aria-label'];
    }
  }, []);

  return (
    <SegmentedControlRoot role="radiogroup" ownerState={ownerState} {...others}>
      {options.map((option, optionIndex) => {
        const value = option.value;
        const selected = getOptionSelected(option);
        const ariaLabel = getOptionAriaLabel(option);
        const disabled = getOptionDisabled(option);
        const labelText = getOptionLabelText(option);
        return (
          <SegmentedControlItem
            key={optionIndex}
            ref={option.ref}
            aria-disabled={disabled}
            ownerState={ownerState}
          >
            {labelText}
            <SegmentedControlItemInput
              aria-label={ariaLabel}
              checked={selected}
              disabled={disabled}
              name={name}
              onChange={event => onChange(value, event)}
              onClick={event => onReselect?.(event, value)}
              ownerState={ownerState}
              ref={inputRef}
              type="radio"
            />
          </SegmentedControlItem>
        );
      })}
    </SegmentedControlRoot>
  );
};

const SegmentedControlRoot = styled('div', {
  target: segmentedControlClasses.root,
})<{ ownerState: SegmentedControlOwnerState }>(({ theme }) => {
  return css`
    display: inline-flex;
    background-color: ${theme.color['interactive/weaker/default']};
    border-radius: 1px;
  `;
});

const SegmentedControlItem = styled('label', {
  target: segmentedControlClasses.item,
})<{ ownerState: SegmentedControlOwnerState }>(({ theme }) => {
  return css`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 80px;
    padding: 5.5px 16px;
    color: ${theme.color['text/weaker/default']};
    ${theme.typography['label/medium/md']}
    border-radius: 1px;
    cursor: pointer;

    &:has(> svg:first-child):has(> svg:nth-last-child(2)):has(> input:nth-child(2)) {
      min-width: 32px;
      padding: 6px;
      font-size: 20px;
    }

    &:has(input:checked) {
      color: ${theme.color['text/default']};
      background-color: ${theme.color['interactive/white/default']};
      box-shadow: inset 0 0 0 1px ${theme.color['border/default']};
    }

    &:hover {
      background-color: ${theme.color['interactive/weak/default']};
    }
    &:active {
      color: ${theme.color['text/weak/default']};
    }
    &:has(input:checked):hover {
      box-shadow: ${theme.mixins.border('border/hover', 1, 'solid', { asBoxShadow: true })};
    }
    &:has(input:checked):active {
      box-shadow: ${theme.mixins.border('border/press', 1, 'solid', { asBoxShadow: true })};
    }

    &:has(input:disabled),
    &:has(input:disabled):hover {
      cursor: initial;
      color: ${theme.color['text/disabled']};
      background-color: ${theme.color['interactive/weaker/disabled']};
    }
    &:has(input:checked:disabled) {
      color: ${theme.color['text/disabled']};
      background-color: ${theme.color['interactive/white/disabled']};
      box-shadow: ${theme.mixins.border('border/disabled', 1, 'solid', { asBoxShadow: true })};
    }

    ${theme.mixins.focusRing('inside', {
      selectors: { focus: '&:has(input:focus-visible)' },
    })}
  `;
});

const SegmentedControlItemInput = styled('input', {
  target: segmentedControlClasses.itemInput,
})<{ ownerState: SegmentedControlOwnerState }>(({ theme }) => {
  return css`
    ${theme.mixins.visuallyHidden}
  `;
});

export default SegmentedControl;
